<template>
  <div>
    <p class="text-pragmatica-extended text-2xl font-bold print:text-xl">
      {{ $t('itemList') }}
    </p>
    <div class="flex flex-col py-4">
      <div class="flex flex-col gap-2">
        <div
          v-for="nido in nidos"
          class="custom-scroll mb-4 flex flex-col overflow-hidden rounded-2xl border border-black bg-white"
        >
          <div class="overflow-auto">
            <table class="w-full table-auto">
              <thead>
                <tr class="text-left">
                  <th colspan="100%">
                    <div
                      class="flex items-center justify-between border-b border-black px-4 pb-2 pt-4"
                    >
                      <div class="flex flex-col justify-center">
                        <p
                          class="text-pragmatica-extended text-xl font-bold print:text-base"
                        >
                          {{
                            isPredefinedSet
                              ? predefinedSet.nidoCode
                              : nido.nidoCode
                          }}
                        </p>
                        <div
                          v-if="userStore.canSeePrices && !isPredefinedSet"
                          class="flex items-center gap-2"
                        >
                          <p class="font-normal print:text-sm">
                            {{ $t('nido.benchPrice') }}
                          </p>
                          <OrderSummaryPrice
                            class="text-lg font-bold print:text-base"
                            :price="
                              getPrice(
                                nido.nidoId,
                                undefined,
                                nido.seatCategory
                              )
                            "
                          />
                        </div>
                      </div>
                      <div
                        v-if="userStore.canSeePrices"
                        class="flex items-center gap-2"
                      >
                        <p class="font-normal print:text-sm">
                          {{ $t('nido.totalPrice') }}
                        </p>
                        <OrderSummaryPrice
                          class="text-lg font-bold print:text-base"
                          :price="getModulePrice(nido)"
                        />
                      </div>
                    </div>
                  </th>
                </tr>
                <tr class="border-b-2 border-black bg-gray-100">
                  <th class="w-1/4 min-w-[150px] print:w-32 print:min-w-full" />
                  <th
                    v-if="nido.parts.left"
                    class="w-1/4 min-w-[168px] align-top print:min-w-[150px]"
                  >
                    <div
                      class="flex flex-col gap-2 p-2 text-left print:text-sm"
                    >
                      {{ $t('nido.position.left') }}
                      <p
                        v-if="nido.parts.left.inverted"
                        class="w-fit rounded-full bg-black px-2 text-white"
                      >
                        {{ $t('nido.position.inverted') }}
                      </p>
                    </div>
                  </th>
                  <th
                    v-if="nido.parts.center"
                    class="w-1/4 min-w-[168px] align-top print:min-w-[150px]"
                  >
                    <div
                      class="flex flex-col gap-2 p-2 text-left print:text-sm"
                    >
                      {{ $t('nido.position.center') }}
                      <p
                        v-if="nido.parts.center.inverted"
                        class="w-fit rounded-full bg-black px-2 text-white"
                      >
                        {{ $t('nido.position.inverted') }}
                      </p>
                    </div>
                  </th>
                  <th
                    v-if="nido.parts.right"
                    class="w-1/4 min-w-[168px] align-top print:min-w-[150px]"
                  >
                    <div
                      class="flex flex-col gap-2 p-2 text-left print:text-sm"
                    >
                      {{ $t('nido.position.right') }}
                      <p
                        v-if="nido.parts.right.inverted"
                        class="w-fit rounded-full bg-black px-2 text-white"
                      >
                        {{ $t('nido.position.inverted') }}
                      </p>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-if="
                    nido.parts.left?.lowBackrest?.highBackrest ||
                    nido.parts.center?.lowBackrest?.highBackrest ||
                    nido.parts.right?.lowBackrest?.highBackrest
                  "
                  class="odd:bg-white even:bg-gray-100"
                >
                  <td
                    class="text-pragmatica-extended hyphens-auto p-2 font-bold print:text-sm"
                  >
                    {{ $t('nido.backrests.high') }}
                  </td>
                  <td v-if="nido.parts.left" class="border-l border-black">
                    <OrderSummaryTexturePart
                      v-if="nido.parts.left.lowBackrest?.highBackrest"
                      :part="nido.parts.left.lowBackrest?.highBackrest"
                      :price="
                        getPrice(
                          nido.parts.left.lowBackrest.highBackrest.id,
                          nido.parts.left.lowBackrest.highBackrest.textureId
                        )
                      "
                      :show-price="!!userStore.canSeePrices"
                    />
                  </td>
                  <td v-if="nido.parts.center" class="border-l border-black">
                    <OrderSummaryTexturePart
                      v-if="nido.parts.center.lowBackrest?.highBackrest"
                      :part="nido.parts.center.lowBackrest?.highBackrest"
                      :price="
                        getPrice(
                          nido.parts.center.lowBackrest.highBackrest.id,
                          nido.parts.center.lowBackrest.highBackrest.textureId
                        )
                      "
                      :show-price="!!userStore.canSeePrices"
                    />
                  </td>
                  <td v-if="nido.parts.right" class="border-l border-black">
                    <OrderSummaryTexturePart
                      v-if="nido.parts.right.lowBackrest?.highBackrest"
                      :part="nido.parts.right.lowBackrest?.highBackrest"
                      :price="
                        getPrice(
                          nido.parts.right.lowBackrest.highBackrest.id,
                          nido.parts.right.lowBackrest.highBackrest.textureId
                        )
                      "
                      :show-price="!!userStore.canSeePrices"
                    />
                  </td>
                </tr>
                <tr
                  v-if="
                    nido.parts.left?.lowBackrest ||
                    nido.parts.center?.lowBackrest ||
                    nido.parts.right?.lowBackrest
                  "
                  class="odd:bg-white even:bg-gray-100"
                >
                  <td
                    class="text-pragmatica-extended hyphens-auto p-2 font-bold print:text-sm"
                  >
                    {{ $t('nido.backrests.low') }}
                  </td>
                  <td v-if="nido.parts.left" class="border-l border-black">
                    <OrderSummaryTexturePart
                      v-if="nido.parts.left.lowBackrest"
                      :part="nido.parts.left.lowBackrest"
                      :price="
                        getPrice(
                          nido.parts.left.lowBackrest.id,
                          nido.parts.left.lowBackrest.textureId
                        )
                      "
                      :show-price="!!userStore.canSeePrices"
                    />
                  </td>
                  <td v-if="nido.parts.center" class="border-l border-black">
                    <OrderSummaryTexturePart
                      v-if="nido.parts.center.lowBackrest"
                      :part="nido.parts.center.lowBackrest"
                      :price="
                        getPrice(
                          nido.parts.center.lowBackrest.id,
                          nido.parts.center.lowBackrest.textureId
                        )
                      "
                      :show-price="!!userStore.canSeePrices"
                    />
                  </td>
                  <td v-if="nido.parts.right" class="border-l border-black">
                    <OrderSummaryTexturePart
                      v-if="nido.parts.right.lowBackrest"
                      :part="nido.parts.right.lowBackrest"
                      :price="
                        getPrice(
                          nido.parts.right.lowBackrest.id,
                          nido.parts.right.lowBackrest.textureId
                        )
                      "
                      :show-price="!!userStore.canSeePrices"
                    />
                  </td>
                </tr>
                <tr
                  v-if="
                    (nido.parts.left &&
                      nido.parts.left.type === NidoPartName.SEAT) ||
                    (nido.parts.center &&
                      nido.parts.center.type === NidoPartName.SEAT) ||
                    (nido.parts.right &&
                      nido.parts.right.type === NidoPartName.SEAT)
                  "
                  class="odd:bg-white even:bg-gray-100"
                >
                  <td
                    class="text-pragmatica-extended hyphens-auto p-2 font-bold print:text-sm"
                  >
                    {{ $t('nido.seat') }}
                  </td>
                  <td v-if="nido.parts.left" class="border-l border-black">
                    <OrderSummaryTexturePart
                      v-if="
                        nido.parts.left &&
                        nido.parts.left.type === NidoPartName.SEAT
                      "
                      :part="nido.parts.left"
                      :price="
                        getPrice(nido.parts.left.id, nido.parts.left.textureId)
                      "
                      :show-price="!!userStore.canSeePrices"
                    />
                  </td>
                  <td v-if="nido.parts.center" class="border-l border-black">
                    <OrderSummaryTexturePart
                      v-if="
                        nido.parts.center &&
                        nido.parts.center.type === NidoPartName.SEAT
                      "
                      :part="nido.parts.center"
                      :price="
                        getPrice(
                          nido.parts.center.id,
                          nido.parts.center.textureId
                        )
                      "
                      :show-price="!!userStore.canSeePrices"
                    />
                  </td>
                  <td v-if="nido.parts.right" class="border-l border-black">
                    <OrderSummaryTexturePart
                      v-if="
                        nido.parts.right &&
                        nido.parts.right.type === NidoPartName.SEAT
                      "
                      :part="nido.parts.right"
                      :price="
                        getPrice(
                          nido.parts.right.id,
                          nido.parts.right.textureId
                        )
                      "
                      :show-price="!!userStore.canSeePrices"
                    />
                  </td>
                </tr>
                <tr
                  v-if="
                    (nido.parts.left &&
                      nido.parts.left.type === NidoPartName.TABLE) ||
                    (nido.parts.center &&
                      nido.parts.center.type === NidoPartName.TABLE) ||
                    (nido.parts.right &&
                      nido.parts.right.type === NidoPartName.TABLE)
                  "
                  class="border-b-2 border-black odd:bg-white even:bg-gray-100"
                >
                  <td
                    class="text-pragmatica-extended hyphens-auto p-2 font-bold print:text-sm"
                  >
                    {{ $t('nido.table') }}
                  </td>
                  <td v-if="nido.parts.left" class="border-l border-black">
                    <OrderSummaryPart
                      v-if="
                        nido.parts.left &&
                        nido.parts.left.type === NidoPartName.TABLE
                      "
                      :part="nido.parts.left"
                      :price="getAccessoryPrice(nido.parts.left.id)"
                      :show-price="!!userStore.canSeePrices"
                    />
                  </td>
                  <td v-if="nido.parts.center" class="border-l border-black">
                    <OrderSummaryPart
                      v-if="
                        nido.parts.center &&
                        nido.parts.center.type === NidoPartName.TABLE
                      "
                      :part="nido.parts.center"
                      :price="getAccessoryPrice(nido.parts.center.id)"
                      :show-price="!!userStore.canSeePrices"
                    />
                  </td>
                  <td v-if="nido.parts.right" class="border-l border-black">
                    <OrderSummaryPart
                      v-if="
                        nido.parts.right &&
                        nido.parts.right.type === NidoPartName.TABLE
                      "
                      :part="nido.parts.right"
                      :price="getAccessoryPrice(nido.parts.right.id)"
                      :show-price="!!userStore.canSeePrices"
                    />
                  </td>
                </tr>
                <tr
                  v-if="
                    nido.parts.left?.lowBackrest?.pillow ||
                    nido.parts.center?.lowBackrest?.pillow ||
                    nido.parts.right?.lowBackrest?.pillow
                  "
                  class="odd:bg-white even:bg-gray-100"
                >
                  <td
                    class="text-pragmatica-extended hyphens-auto p-2 font-bold print:text-sm"
                  >
                    {{ $t('nido.pillow') }}
                  </td>
                  <td v-if="nido.parts.left" class="border-l border-black">
                    <OrderSummaryTexturePart
                      v-if="nido.parts.left.lowBackrest?.pillow"
                      :part="nido.parts.left.lowBackrest?.pillow"
                      :price="
                        nido.type === NidoType.CORNER &&
                        nido.parts.left.lowBackrest?.pillow.id === NidoId.PILLOW
                          ? getPrice(
                              nido.parts.left.lowBackrest?.pillow.id,
                              nido.parts.left.lowBackrest?.pillow.textureId
                            ) * 2
                          : getPrice(
                              nido.parts.left.lowBackrest?.pillow.id,
                              nido.parts.left.lowBackrest?.pillow.textureId
                            )
                      "
                      :show-price="!!userStore.canSeePrices"
                      :is-double="
                        nido.type === NidoType.CORNER &&
                        nido.parts.left.lowBackrest?.pillow.id === NidoId.PILLOW
                      "
                    />
                  </td>
                  <td v-if="nido.parts.center" class="border-l border-black">
                    <OrderSummaryTexturePart
                      v-if="nido.parts.center.lowBackrest?.pillow"
                      :part="nido.parts.center.lowBackrest?.pillow"
                      :price="
                        nido.type === NidoType.CORNER &&
                        nido.parts.center.lowBackrest?.pillow.id ===
                          NidoId.PILLOW
                          ? getPrice(
                              nido.parts.center.lowBackrest?.pillow.id,
                              nido.parts.center.lowBackrest?.pillow.textureId
                            ) * 2
                          : getPrice(
                              nido.parts.center.lowBackrest?.pillow.id,
                              nido.parts.center.lowBackrest?.pillow.textureId
                            )
                      "
                      :show-price="!!userStore.canSeePrices"
                      :is-double="
                        nido.type === NidoType.CORNER &&
                        nido.parts.center.lowBackrest?.pillow.id ===
                          NidoId.PILLOW
                      "
                    />
                  </td>
                  <td v-if="nido.parts.right" class="border-l border-black">
                    <OrderSummaryTexturePart
                      v-if="nido.parts.right.lowBackrest?.pillow"
                      :part="nido.parts.right.lowBackrest?.pillow"
                      :price="
                        nido.type === NidoType.CORNER &&
                        nido.parts.right.lowBackrest?.pillow.id ===
                          NidoId.PILLOW
                          ? getPrice(
                              nido.parts.right.lowBackrest?.pillow.id,
                              nido.parts.right.lowBackrest?.pillow.textureId
                            ) * 2
                          : getPrice(
                              nido.parts.right.lowBackrest?.pillow.id,
                              nido.parts.right.lowBackrest?.pillow.textureId
                            )
                      "
                      :show-price="!!userStore.canSeePrices"
                      :is-double="
                        nido.type === NidoType.CORNER &&
                        nido.parts.right.lowBackrest?.pillow.id ===
                          NidoId.PILLOW
                      "
                    />
                  </td>
                </tr>
                <tr
                  v-if="
                    nido.parts.left?.socket ||
                    nido.parts.center?.socket ||
                    nido.parts.right?.socket
                  "
                  class="odd:bg-white even:bg-gray-100"
                >
                  <td
                    class="text-pragmatica-extended hyphens-auto p-2 font-bold print:text-sm"
                  >
                    {{ $t('nido.socket.title') }}
                  </td>
                  <td v-if="nido.parts.left" class="border-l border-black">
                    <OrderSummaryPart
                      v-if="nido.parts.left.socket"
                      :part="nido.parts.left.socket"
                      :price="getAccessoryPrice(nido.parts.left.socket.id)"
                      :show-price="!!userStore.canSeePrices"
                    />
                  </td>
                  <td v-if="nido.parts.center" class="border-l border-black">
                    <OrderSummaryPart
                      v-if="nido.parts.center.socket"
                      :part="nido.parts.center.socket"
                      :price="getAccessoryPrice(nido.parts.center.socket.id)"
                      :show-price="!!userStore.canSeePrices"
                    />
                  </td>
                  <td v-if="nido.parts.right" class="border-l border-black">
                    <OrderSummaryPart
                      v-if="nido.parts.right.socket"
                      :part="nido.parts.right.socket"
                      :price="getAccessoryPrice(nido.parts.right.socket.id)"
                      :show-price="!!userStore.canSeePrices"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    <GliderTypeSelector
      v-if="editable"
      class="min-h-[6rem]"
      :glider-type="gliderType"
      :module-count="moduleCount"
      :glider-price="gliderUnitPrice"
      @glider-type-changed="(newType) => emit('glider-type-changed', newType)"
    />
    <div v-else-if="gliderType !== NidoGliderType.NONE" class="mb-10 flex">
      <p
        class="text-pragmatica-extended min-w-fit text-xl font-bold print:text-base"
      >
        {{ $t('nido.glider.title') }}
        {{ $t('nido.glider.type.' + gliderType) }} {{ moduleCount }}x
      </p>
      <span class="mx-4 mb-2 w-full border-b border-dotted border-black" />
      <OrderSummaryPrice
        class="text-pragmatica-extended min-w-fit text-xl font-bold print:text-base"
        :price="getGliderPrice()"
      />
    </div>
    <div v-if="connectorCount !== 0" class="mb-10 flex">
      <p
        class="text-pragmatica-extended min-w-fit text-xl font-bold print:text-base"
      >
        {{ $t('nido.connects') }} {{ connectorCount }}x
      </p>
      <span class="mx-4 mb-2 w-full border-b border-dotted border-black" />
      <OrderSummaryPrice
        class="text-pragmatica-extended min-w-fit text-xl font-bold print:text-base"
        :price="getConnectorPrice()"
      />
    </div>
    <div v-if="userStore.canSeePrices" class="flex">
      <p
        class="text-pragmatica-extended min-w-fit text-2xl font-bold print:text-xl"
      >
        {{ $t('nido.totalPrice') }}
      </p>
      <span class="mx-4 mb-2 w-full border-b border-dotted border-black" />
      <OrderSummaryPrice
        class="text-pragmatica-extended min-w-fit text-2xl font-bold print:text-xl"
        :price="setPrice"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { useUser } from '~/stores/user'
import type { ProductPrices } from '~/types/api'
import {
  NidoGliderId,
  NidoGliderType,
  NidoId,
  NidoPartName,
  NidoSocketId,
  NidoType,
} from '~/service/experience/world/nido/enum'
import { Nido } from '~/service/experience/world/nido/Nido'
import { useSurfaces } from '~/stores/surfaces'
import { NidoSeat } from '~/service/experience/world/nido/parts/NidoTexturePart'
import NidoTable from '~/service/experience/world/nido/parts/NidoTable'
import GliderTypeSelector from '~/components/order/GliderTypeSelector.vue'

const userStore = useUser()
const surfacesStore = useSurfaces()
const api = useApi()

await surfacesStore.init()
await userStore.init()

const prices = ref<ProductPrices[]>([])

const { canSeePrices } = storeToRefs(userStore)

onMounted(async () => {
  await nextTick()

  if (userStore.canSeePrices) {
    prices.value = (await api.getPrices()) || []
  }
})

watch(canSeePrices, async () => {
  if (canSeePrices.value) {
    prices.value = (await api.getPrices()) || []
  } else {
    prices.value = []
  }
})

const props = defineProps<{
  nidos: Nido[]
  circleNidoConfigured: boolean
  editable: boolean
  gliderType: NidoGliderType
}>()

const emit = defineEmits(['glider-type-changed'])

const { predefinedSet } = usePredefinedSet(props.nidos)

const isPredefinedSet = computed(() => {
  return predefinedSet.value !== null
})

const connectorCount = computed(() => {
  let count = moduleCount.value
  if (!props.circleNidoConfigured) {
    // subtract one from count of nidos if there are two dead ends
    count--
  }
  props.nidos.forEach((nido) => {
    if (nido.type === NidoType.CORNER) {
      const neighboursCount = nido.neighborIds.filter(
        (id: string | null) => id !== null
      ).length
      count -= neighboursCount
    }
  })
  return count
})

function getConnectorPrice() {
  return connectorCount.value * getAccessoryPrice(NidoId.CONNECT)
}

function getPositionPrice(part: NidoTable | NidoSeat, type: NidoType) {
  let price = 0
  if (part.type === NidoPartName.SEAT) {
    price += getPrice(part.id, part.textureId)
  } else {
    price += getAccessoryPrice(part.id)
  }
  if (part.lowBackrest) {
    price += getPrice(part.lowBackrest.id, part.lowBackrest.textureId)
    if (part.lowBackrest.pillow) {
      const pillowPrice = getPrice(
        part.lowBackrest.pillow.id,
        part.lowBackrest.pillow.textureId
      )
      if (
        type === NidoType.CORNER &&
        part.lowBackrest?.pillow.id === NidoId.PILLOW
      ) {
        price *= 2
      }
      price += pillowPrice
    }
    if (part.lowBackrest.highBackrest) {
      price += getPrice(
        part.lowBackrest.highBackrest.id,
        part.lowBackrest.highBackrest.textureId
      )
    }
  }
  if (part.socket) {
    price += getAccessoryPrice(part.socket.id)
  }

  return price
}

function getModulePrice(nido: Nido) {
  let price = 0

  if (isPredefinedSet && predefinedSet.value) {
    return getPredefinedSetPrice()
  }
  if (nido.parts.left) {
    price += getPositionPrice(nido.parts.left, nido.type)
  }
  if (nido.parts.center) {
    price += getPositionPrice(nido.parts.center, nido.type)
  }
  if (nido.parts.right) {
    price += getPositionPrice(nido.parts.right, nido.type)
  }
  price += getPrice(nido.nidoId, undefined, nido.seatCategory)

  return price
}

function getPredefinedSetPrice() {
  const nido = props.nidos[0]
  let price = 0
  if (isPredefinedSet && predefinedSet.value) {
    price += getPrice(predefinedSet.value.id, undefined, nido.seatCategory)
    if (nido.parts.left?.lowBackrest?.pillow) {
      price += getPrice(
        nido.parts.left?.lowBackrest?.pillow.id,
        nido.parts.left?.lowBackrest?.pillow.textureId
      )
    }
    if (nido.parts.center?.lowBackrest?.pillow) {
      price += getPrice(
        nido.parts.center?.lowBackrest?.pillow.id,
        nido.parts.center?.lowBackrest?.pillow.textureId
      )
    }
    if (nido.parts.right?.lowBackrest?.pillow) {
      price += getPrice(
        nido.parts.right?.lowBackrest?.pillow.id,
        nido.parts.right?.lowBackrest?.pillow.textureId
      )
    }
    if (nido.parts.left?.socket) {
      price += getAccessoryPrice(nido.parts.left?.socket.id)
    }
    if (nido.parts.center?.socket) {
      price += getAccessoryPrice(nido.parts.center?.socket.id)
    }
    if (nido.parts.right?.socket) {
      price += getAccessoryPrice(nido.parts.right?.socket.id)
    }
    return price + getGliderPrice()
  }
}

const setPrice = computed(() => {
  if (isPredefinedSet && predefinedSet.value) {
    return getPredefinedSetPrice()
  }

  return (
    props.nidos
      .map((nido) => getModulePrice(nido))
      .reduce((acc, curr) => {
        return acc + curr
      }, 0) +
    getConnectorPrice() +
    getGliderPrice()
  )
})

function getPrice(
  id: NidoId | NidoSocketId | NidoGliderId,
  textureId?: number,
  seatCategory?: number | null
) {
  if (predefinedSet.value !== null) {
    // for predefined sets prices of low and high backrest are already included in price of set
    if (
      id === NidoId.LOW_BACKREST_CORNER ||
      id === NidoId.LOW_BACKREST ||
      id === NidoId.HIGH_BACKREST_CORNER ||
      id === NidoId.HIGH_BACKREST
    ) {
      return 0
    }
  }

  // allow null value for accessories which do not have fabric category
  let category = seatCategory === undefined ? 116 : seatCategory

  if (textureId) {
    category = surfacesStore.getSurfaceCategory(textureId)
  }

  return (
    prices.value
      .find((price) => price.nid === id)
      ?.prices?.find((price) => price.surfaceCollectionCategoryNid === category)
      ?.price || 0
  )
}

function getAccessoryPrice(id: NidoId | NidoSocketId | NidoGliderId) {
  return getPrice(id, undefined, null)
}

const moduleCount = computed(() => props.nidos.length)

const gliderUnitPrice = computed(() => {
  if (props.gliderType === NidoGliderType.NONE) {
    return 0
  }
  return getAccessoryPrice(NidoGliderId[props.gliderType])
})

function getGliderPrice() {
  return gliderUnitPrice.value * moduleCount.value
}
</script>

<style scoped></style>
